if (!customElements.get('wickelplatz-cart-upsell')) {
  customElements.define('wickelplatz-cart-upsell', class WickelplatzCartUpsell extends HTMLElement {
    getModeLabels() {
      switch (this.dataset.mode) {
        case 'regal':
          return { singular: this.dataset.labelShelf };
        case 'aufsatz':
          return { singular: this.dataset.labelAttachment };
        default:
          return { singular: this.dataset.labelMat };
      }
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.labels = this.getModeLabels();

      this.dismissButton = this.querySelector('[data-wickelplatz-dismiss]');
      if (this.dataset.complete !== 'true' && this.isDismissed()) {
        this.hidden = true;
        return;
      }
      if (this.dismissButton) {
        this.dismissButton.addEventListener('click', () => this.dismiss());
      }
      if (this.dataset.complete === 'true') return;

      const dataElement = this.querySelector('[data-wickelplatz-data]');
      this.select = this.querySelector('[data-wickelplatz-select]');
      this.quantityInputs = Array.from(this.querySelectorAll('[data-wickelplatz-quantity]'));
      this.button = this.querySelector('[data-wickelplatz-add]');
      this.message = this.querySelector('[data-wickelplatz-message]');

      try {
        this.variants = JSON.parse(dataElement ? dataElement.textContent : '[]');
      } catch (error) {
        this.variants = [];
      }

      if (!this.select || !this.button || this.variants.length === 0) {
        this.hidden = true;
        return;
      }

      this.select.addEventListener('change', () => this.updateRecommendation());
      this.button.addEventListener('click', () => this.addSelectedVariant());
      this.updateRecommendation();
    }

    getDismissStorageKey() {
      return 'wickelplatz-upsell-dismissed:' + (this.dataset.dismissKey || this.dataset.expectedSize || 'current');
    }

    isDismissed() {
      try {
        return window.sessionStorage.getItem(this.getDismissStorageKey()) === 'true';
      } catch (error) {
        return false;
      }
    }

    dismiss() {
      try {
        window.sessionStorage.setItem(this.getDismissStorageKey(), 'true');
      } catch (error) {
        // The recommendation is still hidden for the current rendered cart.
      }

      this.hidden = true;
      const screenReader = document.getElementById('screen-reader-info');
      if (screenReader) {
        screenReader.textContent = this.dataset.textDismissed;
        setTimeout(() => {
          screenReader.textContent = '';
        }, 1500);
      }
    }

    getSelectedQuantity() {
      const selectedQuantity = this.quantityInputs
        ? this.quantityInputs.find((input) => input.checked)
        : null;
      const quantity = selectedQuantity ? parseInt(selectedQuantity.value, 10) : 1;
      if (!Number.isFinite(quantity) || quantity < 1) return 1;
      return Math.min(quantity, 2);
    }

    updateRecommendation() {
      const variant = this.variants.find(item => String(item.id) === String(this.select.value)) || this.variants[0];
      if (!variant) return;

      this.selectedVariant = variant;
      const image = this.querySelector('[data-wickelplatz-image]');
      const title = this.querySelector('[data-wickelplatz-title]');
      const design = this.querySelector('[data-wickelplatz-design]');
      const size = this.querySelector('[data-wickelplatz-size]');
      const price = this.querySelector('[data-wickelplatz-price]');

      if (image) {
        image.src = variant.image;
        image.alt = variant.imageAlt || variant.title;
        image.hidden = !variant.image;
      }
      if (title) title.textContent = variant.title;
      if (design) design.textContent = variant.design ? this.dataset.textDesignPrefix + variant.design : '';
      if (size) size.textContent = (variant.sizePrefix || this.dataset.textSizePrefix) + variant.size;
      if (price) price.textContent = variant.price;
      const ariaTemplate = variant.size
        ? this.dataset.textAriaAddTemplate
        : this.dataset.textAriaAddWithoutSizeTemplate;
      this.button.setAttribute(
        'aria-label',
        ariaTemplate
          .replace('__TITLE__', variant.title)
          .replace('__SIZE__', variant.size || '')
      );
    }

    async addSelectedVariant() {
      if (this.busy || !this.selectedVariant) return;
      this.busy = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      const quantity = this.getSelectedQuantity();
      this.message.textContent = this.dataset.textAddingTemplate.replace('__PRODUCT__', this.labels.singular);
      const genericError = window.KROWN?.settings?.locales?.cart_general_error
        || this.dataset.textGenericError;

      try {
        const body = new URLSearchParams({
          id: String(this.selectedVariant.id),
          quantity: String(quantity)
        });

        const shopifyRoot = window.Shopify?.routes?.root || '/';
        const cartAddUrl = shopifyRoot.replace(/\/?$/, '/') + 'cart/add.js';
        const addResponse = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: body.toString()
        });

        const addText = await addResponse.text();
        let addResult;
        try {
          addResult = JSON.parse(addText);
        } catch (error) {
          throw new Error(genericError);
        }
        if (!addResponse.ok || addResult.status === 422) {
          throw new Error(addResult.description || addResult.message || genericError);
        }

        const helperResponse = await fetch('?section_id=helper-cart');
        if (!helperResponse.ok) throw new Error(genericError);
        const helperText = await helperResponse.text();

        if (typeof window.renderHelperCart !== 'function') {
          throw new Error(genericError);
        }

        window.renderHelperCart(helperText);

        const cartForm = document.getElementById('AjaxCartForm');
        if (cartForm) cartForm.dispatchEvent(new Event('cart-updated'));

        if (window.KROWN && KROWN.functions) {
          KROWN.functions.eventDispatcher('krown:cart:add', {
            variantId: String(this.selectedVariant.id),
            quantity: quantity,
            source: 'wickelplatz-cart-upsell'
          });

          if (typeof KROWN.functions.dispatchStandardEvent === 'function') {
            KROWN.functions.dispatchStandardEvent('CartLinesUpdateEvent', document, {
              action: 'add',
              context: 'cart',
              lines: [{
                merchandiseId: String(this.selectedVariant.id),
                quantity: quantity
              }],
              promise: Promise.resolve({ cart: null })
            });
          }
        }

        const screenReader = document.getElementById('screen-reader-info');
        if (screenReader) {
          screenReader.textContent = this.dataset.textAddedTemplate.replace('__PRODUCT__', this.labels.singular);
          setTimeout(() => {
            screenReader.textContent = '';
          }, 1500);
        }
      } catch (error) {
        this.busy = false;
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
        this.message.textContent = error && error.message
          ? error.message
          : genericError;
      }
    }
  });
}